import React, { useState, useMemo } from 'react'
import './order-refund.scss'
import { IMallOrderDetail } from '../order-list/order-detail/type'
import { Column, Row } from '@/components/layout/layout'
import { setObj, deepClone, INPUT_PRICE_LIMIT_REGEXP } from '@/utils'
import LayoutInput from '../../../goods-manage/components/form/layout-input/layout-input'
import { ColumnsType } from 'antd/lib/table'
import { Radio, Table, Checkbox } from 'antd'
import { BaseDialog } from '@/components/dialog/base-dialog'
import Button from '@/components/button/button'
import { layerManage, showLayer } from '@/utils/layer'
import RemarkPopover from '../../../goods-manage/components/remark-popover/remark-popover'
import Iconfont from '@/components/iconfont/iconfont'
import classnames from 'classnames'
import useBemClass from '@/utils/hooks/useBEMClass'
import { useUpdateEffect } from 'ahooks'
import Big from 'big.js'
import MallOrderRefundConfirm from './refund-confirm'

interface MallOrderRefundProps {
    goodList: IMallOrderDetail.OrderGoodsList[]
    payments: IMallOrderDetail.Payment[]
    orderId: number
    orderType: number
    deliverFee?: number
    done: boolean
}

export interface RefundMallGoodsList extends IMallOrderDetail.OrderGoodsList {
    refund_amount: number
    refund_subtotal: number
    return_stock: number
}

const MallOrderRefund: React.FC<MallOrderRefundProps> = props => {
    const cls = useBemClass('mall-order-refund')
    const [remark, setRemark] = useState<string>('')
    const [allVal, setAllVal] = useState<number>(1)
    const [refundDeliver, setrefundDeliver] = useState<boolean>(false)

    const [refundGoods, setRefundGoods] = useState<RefundMallGoodsList[]>(
        props.goodList.map(g => ({
            ...g,
            refund_amount: props.done ? g.delivered_amount : g.amount,
            refund_subtotal: +Big(g.actual_payment).plus(g.reweight_payment),
            return_stock: 1,
        })),
    )

    useUpdateEffect(() => {
        const isRefundStock = refundGoods.every(r => r.return_stock === 1)
        const isOnlyRefundMoney = refundGoods.every(r => r.return_stock === 0)

        setAllVal(isRefundStock ? 1 : isOnlyRefundMoney ? 0 : 2)
    }, [refundGoods])

    // 总退款金额
    const totalRefundAmount = useMemo(() => {
        return refundGoods.reduce((pre, next) => {
            return next.return_stock === 2 ? pre : pre.plus(next.refund_subtotal)
        }, Big(0))
    }, [refundGoods])

    const columns: ColumnsType<RefundMallGoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            ellipsis: true,
            render: (v, r) => (
                <Column>
                    <div>{r.goods_name}</div>
                    {r.price_annotation && (
                        <div data-margin-top-2 data-color-999 data-font-size-12>
                            {r.price_annotation}
                        </div>
                    )}
                </Column>
            ),
        },
        {
            title: '售价',
            key: 'supplier',
            render: (v, r) => r.actual_payment,
        },
        {
            title: '数/重量',
            width: 70,
            dataIndex: 'amount',
            render: (val, r) => r.amount + r.goods_unit.name,
        },
        {
            title: '实付',
            key: 'actual_payment',
            dataIndex: 'actual_payment',
            align: 'right',
            render: (v, r) => `¥${+Big(r.actual_payment).plus(r.reweight_payment)}`,
        },
        {
            title: '退款方式',
            width: 340,
            key: 'return_stock',
            dataIndex: 'return_stock',
            align: 'center',
            render: (val, record, index) => {
                return (
                    <Row>
                        <Radio.Group
                            onChange={e => {
                                setRefundGoods(
                                    deepClone(setObj(refundGoods, `[${index}].return_stock`, e.target.value)),
                                )
                            }}
                            value={val}
                        >
                            <Radio value={0}>仅退款</Radio>
                            <Radio value={1}>退款退库存</Radio>
                            <Radio value={2}>不退款</Radio>
                        </Radio.Group>
                    </Row>
                )
            },
        },
        {
            title: '退货量',
            key: 'refund_amount',
            width: 130,
            dataIndex: 'refund_amount',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        placeholder="请录入"
                        maxNum={record.delivered_amount || record.amount}
                        onBlur={e => {
                            const amount = parseFloat(e) || 0

                            setRefundGoods(
                                deepClone(
                                    setObj(
                                        setObj(refundGoods, `[${index}].refund_amount`, amount),
                                        `[${index}].refund_subtotal`,
                                        +Big(+Big(record.actual_payment).plus(record.reweight_payment))
                                            .div(props.done ? record.delivered_amount : record.amount)
                                            .times(amount)
                                            .round(2),
                                    ),
                                ),
                            )
                        }}
                    />
                    {record.goods_unit.name}
                </Row>
            ),
        },
        {
            title: '退货金额',
            key: 'refund_subtotal',
            width: 130,
            dataIndex: 'refund_subtotal',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        placeholder="请录入"
                        valueRegExp={INPUT_PRICE_LIMIT_REGEXP}
                        maxNum={
                            record.delivered_amount
                                ? +Big(record.price).times(record.delivered_amount)
                                : +Big(record.actual_payment).plus(record.reweight_payment)
                        }
                        onBlur={e => {
                            const amount = parseFloat(e) || 0

                            setRefundGoods(
                                deepClone(
                                    setObj(
                                        setObj(refundGoods, `[${index}].refund_subtotal`, amount),
                                        `[${index}].refund_amount`,
                                        +Big(amount)
                                            .div(+Big(record.actual_payment).plus(record.reweight_payment))
                                            .times(props.done ? record.delivered_amount : record.amount)
                                            .round(3),
                                    ),
                                ),
                            )
                        }}
                    />
                    元
                </Row>
            ),
        },
    ]

    return (
        <BaseDialog isShow width={993} title="退款" onClose={() => layerManage.pop()}>
            <div className={cls('table-container')}>
                <Table
                    columns={columns}
                    rowKey={r => r.order_goods_id}
                    dataSource={refundGoods}
                    pagination={false}
                    bordered
                    scroll={{ x: 'max-content', y: 225 }}
                ></Table>
                <div className={cls('tab')}>
                    <Row data-align-items-center data-color-333 data-font-size-14>
                        共
                        <span className="them-color" data-margin-h-4>
                            {refundGoods.length}
                        </span>
                        件
                    </Row>
                    <div
                        style={{
                            marginLeft: '80px',
                        }}
                    >
                        <Radio.Group
                            onChange={e => {
                                const val = e.target.value
                                setRefundGoods(good => {
                                    return good.map(g => ({
                                        ...g,
                                        return_stock: val,
                                    }))
                                })
                                setAllVal(e.target.value)
                            }}
                            value={allVal}
                        >
                            <Radio value={0}>全部仅退款</Radio>
                            <Radio value={1}>全部退款退库存</Radio>
                        </Radio.Group>
                    </div>
                    <Row data-align-items-center data-color-333 data-font-size-14>
                        退
                        <span className="danger-color" data-margin-left-4 data-font-weight-600>
                            ¥{+totalRefundAmount.round(2)}
                        </span>
                    </Row>
                </div>
                <div className={cls('bottom')}>
                    <RemarkPopover
                        value={remark}
                        maxLen={50}
                        onConfirm={e => {
                            setRemark(e)
                        }}
                    >
                        {remark ? (
                            <Row data-align-items-center>
                                <Row>
                                    <span data-color-333 data-font-size-16>
                                        备注：
                                    </span>
                                    <div className={classnames(cls('remark'), 'two-line')}>{remark}</div>
                                </Row>
                                <Iconfont name="icon6youjiantou-20" size={16} />
                            </Row>
                        ) : (
                            <Row data-align-items-center data-color-333 data-font-size-16 data-cursor-pointer>
                                <Iconfont name="icon6cunweicaogao-20" size={20} />
                                <span
                                    style={{
                                        color: '#3C90FF',
                                        marginLeft: '6px',
                                    }}
                                >
                                    添加备注
                                </span>
                            </Row>
                        )}
                    </RemarkPopover>
                    <Row data-algin-items-center>
                        {!!props.deliverFee && (
                            <div data-margin-top-10 data-margin-right-10>
                                <Checkbox
                                    onChange={e => {
                                        setrefundDeliver(e.target.checked)
                                    }}
                                >
                                    退配送费 ¥{props.deliverFee}
                                </Checkbox>
                            </div>
                        )}
                        <Button
                            types="danger"
                            onClick={() => {
                                layerManage.pop()
                                showLayer(
                                    <MallOrderRefundConfirm
                                        orderId={props.orderId}
                                        orderType={props.orderType}
                                        refundGoods={refundGoods}
                                        remark={remark}
                                        subtotal={+totalRefundAmount.round(2)}
                                        payments={props.payments}
                                        deliveryFee={refundDeliver ? props.deliverFee : 0}
                                    />,
                                )
                            }}
                        >
                            确认
                        </Button>
                    </Row>
                </div>
            </div>
        </BaseDialog>
    )
}

export default MallOrderRefund
